import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from 'react-router-dom';
import { CommonContext } from "../../../contexts/common/commonContext";
import {
   LocationOption,
   LocationOrAccountOptions,
   OpportunityLocatorViews,
   SortBy,
} from "../../../consts";
import { DateKind } from "../../Form/Inputs/Generic/FormDatePicker/DatePicker/DateKind";
import { getEndOfDay, getStartOfDay } from "../../../../utils/dateUtil";
import { EnvironmentContext } from "app/contexts/environment/environmentContext";
import { parseURLParams } from "app/hooks/useUrlParams";
import { OpportunityLocatorViewContext } from "../context/OpportunityLocatorViewContext";

export interface IOpportunitySearchFilter {
   searchType: LocationOrAccountOptions;
   accountName: string;
   searchOrigin: string;
   radiusMiles: string;
   startDate: Date;
   endDate: Date;
   locationType: LocationOption;
   selectedAppointmentTypes: string[] | null;
   sortBy: SortBy;
   dateKind: DateKind;
   accountCode: string;  // SCREDP-42: new search parameters
   locationCode: string;
   isPrivate: Boolean;
}

export interface IUseOpportunitySearchFilter {
   searchFilters: IOpportunitySearchFilter;
   updateFilter: (update: Partial<IOpportunitySearchFilter>) => void;
}

export const defaultOpportunitySearchFilter: IOpportunitySearchFilter = {
   searchType: LocationOrAccountOptions.LOCATION,
   searchOrigin: "",
   accountName: "",
   radiusMiles: "10",
   startDate: getStartOfDay(),
   endDate: getEndOfDay(),
   locationType: LocationOption.ALL,
   selectedAppointmentTypes: [],
   sortBy: SortBy.CHRONOLOGICAL_ORDER,
   dateKind: DateKind.For_Today,
   accountCode: "",
   locationCode: "",
   isPrivate: false,
};

export function useOpportunitySearchFilter(): IUseOpportunitySearchFilter {
   const [searchParams] = useSearchParams(); // SCREDP-42: Updating filter logic to use URL parameters (searchParams) for initial values 
   const { defaultSearchOrigin, currentOriginFromBrowser } = useContext(CommonContext); //SCREDP-87 Include all of Zip and Allow Street Address as Default
   const { environmentConfig } = useContext(EnvironmentContext);
   const { changeLayout } = useContext(OpportunityLocatorViewContext);

   const urlParams = useMemo(() => parseURLParams(Object.fromEntries(searchParams)), [searchParams]);
   const origin = currentOriginFromBrowser ? currentOriginFromBrowser : defaultSearchOrigin;  //SCREDP-87 Include all of Zip and Allow Street Address as Default
   const defaultRadius = (environmentConfig?.defaultRadius ? environmentConfig?.defaultRadius + "" : "10");

   const getInitialFilters = useCallback(() => {
      if (!urlParams) {
         return {
            ...defaultOpportunitySearchFilter,
            searchOrigin: `${origin}`,
            radiusMiles: defaultRadius, // set radiusMiles with the value defined in the admin portal (SCREDP-92)
            startDate: getStartOfDay(),
            endDate: getEndOfDay(),
            sortBy: SortBy.CHRONOLOGICAL_ORDER,
            locationType: LocationOption.ALL,
            selectedAppointmentTypes: [],
            dateKind: DateKind.For_Today,
         }
      }

      return {
         ...defaultOpportunitySearchFilter,
         searchType: urlParams.origin ? LocationOrAccountOptions.LOCATION : LocationOrAccountOptions.ACCOUNT,
         searchOrigin: urlParams.origin ? urlParams.origin : `${origin}`,
         radiusMiles: urlParams.radiusMiles || defaultRadius, 
         accountName: urlParams.accountName || "",
         startDate: urlParams.startDate || getStartOfDay(),
         endDate: urlParams.endDate || getEndOfDay(),
         sortBy: SortBy.CHRONOLOGICAL_ORDER,
         locationType: urlParams.locationType || LocationOption.ALL,
         selectedAppointmentTypes: urlParams.donationType || [],
         dateKind: urlParams.dateKind || DateKind.For_Today,
         accountCode: urlParams.accountCode || "",
         locationCode: urlParams.locationCode || "",
         isPrivate: urlParams.isPrivate || false
      };
   }, [urlParams, origin, defaultRadius]); // QA-1293/SCREDP-42: Added missing dependencies and changed to callback
   
   const [searchFilters, setSearchFilters] = useState<IOpportunitySearchFilter>(getInitialFilters());
   // QA-1293/SCREDP-42: Update filters when dependencies change
   useEffect(() => {
      setSearchFilters(getInitialFilters());
   }, [getInitialFilters]);

   // SCREDP-42: Update filter values when URL parameters are present
   useEffect(() => {
      if (urlParams && (urlParams.accountName || urlParams.origin || urlParams.accountCode || urlParams.locationCode)) {
         setSearchFilters(current => ({
            ...current,
            searchType: urlParams.origin ? LocationOrAccountOptions.LOCATION : LocationOrAccountOptions.ACCOUNT,
            accountName: urlParams.accountName,
            accountCode: urlParams.accountCode,
            locationCode: urlParams.locationCode,
            selectedAppointmentTypes: urlParams.donationType,
            startDate: urlParams.startDate,
            endDate: urlParams.endDate,
            searchOrigin: urlParams.origin,
            locationType: urlParams.locationType,
            radiusMiles: urlParams.radiusMiles || defaultRadius,
            isPrivate: urlParams.isPrivate,
            dateKind: urlParams.dateKind,
         }));
         changeLayout(OpportunityLocatorViews.FULL_MAP_EXPANDED_LAYOUT); 
      }
   }, [urlParams, defaultRadius, changeLayout]); // SCREDP-42: Added defaultRadius and changeLayout dependencies. This change was created using Claude AI and is not totally understood currnently. Removing this code will break the opportunity locator


   const updateFilter = (update: Partial<IOpportunitySearchFilter>) => {
      setSearchFilters({ ...searchFilters, ...update });
   };

   return {
      searchFilters,
      updateFilter,
   };
}